import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { HelpdeskLink } from "@/features/helpdesk"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Lightbulb } from "lucide-react"
import { forwardRef, useImperativeHandle, useState } from "react"

/**
 * AUTO-OPENS UNTIL DISMISSED ONCE, THEN NEVER AGAIN. Two layers:
 * - module flag: at most one auto-open per page load, surviving workspace
 *   remounts (leaving a declaration and coming back, switching tabs).
 * - localStorage: once the reviewer actively closed it (Verstanden, outside
 *   click, Esc), no future page load auto-opens either: a daily reviewer
 *   otherwise met this popover on every reload and every new declaration
 *   after one (product-owner, 30.08.). The bulb stays the manual way back.
 * try/catch: storage can be unavailable (private mode); then the module
 * flag alone applies and behavior degrades to once per page load.
 */
let autoOpened = false

const HELP_SEEN_KEY = "mc.sv2.helpDismissed"

function helpDismissed(): boolean {
  try {
    return window.localStorage.getItem(HELP_SEEN_KEY) === "1"
  } catch {
    return false
  }
}

function markHelpDismissed(): void {
  try {
    window.localStorage.setItem(HELP_SEEN_KEY, "1")
  } catch {
    /* per-viewer convenience only */
  }
}

export interface HelpPopoverHandle {
  /** `?` — `useStudioV2Keyboard`'s own trigger, driving the SAME open state
   *  the bulb button does rather than a second one. */
  toggle: () => void
}

export interface HelpPopoverProps {
  /** Adds the W ("Weiter") row to the shortcut list. `StudioV2Layout`
   *  always passes it now that the guided layer is standard (TUR-1211);
   *  kept optional here so this popover still renders without it.
   *  (The Ausführlich/Kompakt density toggle was cut from this increment —
   *  product call 31.08., verbose-only for now.) */
  showNextShortcut?: boolean
}

/**
 * The bulb help control — 3 steps + the final keyboard shortcut set (B11:
 * ↑↓ framework nav, ↵, 1/2/3, ?, Esc). Opens itself once, the reviewer's
 * first time on the workspace this session; every later visit starts closed.
 *
 * Markup follows the prototype's `helpPop()` (`.ht` title, `.helpsteps`,
 * `.hkeys`, `.hf` footer). BUILT ON THE SHARED `Popover` + `disablePortal` —
 * the same escape hatch `SupplierAssignControl` uses for the identical
 * problem: Radix portals its content to `document.body`, OUTSIDE `.sv2-root`
 * — where every `--card`/`--fg*` design variable this popover paints with is
 * undefined, so the card would render transparent over the document pane.
 * `disablePortal` keeps the DOM node inside `.sv2-root`'s variable scope;
 * Radix's own floating-ui math still positions it correctly regardless of
 * where in the tree it renders. `.sv2-helppop`'s own `position:absolute`
 * is cancelled (`style={{ position: "static" }}`) so it does not fight
 * Radix's positioning of the actual box — `align="end"` reproduces the
 * CSS's old `right:0` (right-anchored to the bulb button), `sideOffset={10}`
 * the old `top:calc(100% + 10px)`.
 *
 * WHY NOT THE HAND-ROLLED VERSION ANY MORE. It worked, but re-implemented
 * outside-click and Escape dismissal by hand instead of reusing Radix's
 * `DismissableLayer` (focus is never trapped or returned to the trigger on
 * close, and the ad hoc listeners are one more thing to keep correct). Radix
 * gives that back for free; only the Escape-swallow ordering below needed a
 * matching bridge to keep working.
 */
export const HelpPopover = forwardRef<HelpPopoverHandle, HelpPopoverProps>(
  function HelpPopover({ showNextShortcut = false }, ref) {
    const { translate } = useTranslation()
    const [open, setOpen] = useState(() => {
      if (autoOpened || helpDismissed()) return false
      autoOpened = true
      return true
    })
    // Any close counts as "read it": Verstanden, outside click and Esc all
    // arrive here through the same onOpenChange(false).
    const handleOpenChange = (next: boolean) => {
      if (!next) markHelpDismissed()
      setOpen(next)
    }

    useImperativeHandle(ref, () => ({
      toggle: () =>
        setOpen((o) => {
          if (o) markHelpDismissed()
          return !o
        }),
    }))

    return (
      <div className="sv2-help">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={translate("MC_DECL_V2_HELP_TRIGGER_ARIA")}
              aria-expanded={open}
              className={cn("sv2-helpbtn", open && "sv2-on")}
            >
              <Lightbulb size={15} className="sv2-ic" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            disablePortal
            align="end"
            sideOffset={10}
            className="w-[420px] rounded-none border-0 bg-transparent p-0 shadow-none"
            // CAPTURE-PHASE, NOT A PLAIN BUBBLE HANDLER. Radix's own Escape
            // handling (`useEscapeKeydown`) already listens on `document`
            // with `{capture: true}` — see
            // `@radix-ui/react-use-escape-keydown`. `stopPropagation` here
            // runs INSIDE that same document-capture listener, before the
            // event ever reaches `window`'s bubble-phase listener
            // (`useStudioV2Keyboard`'s Esc-to-list). Not `preventDefault`:
            // that would also cancel Radix's own `onDismiss` (the close),
            // which must still fire — only the window listener must never
            // see this keypress. Exactly the hand-rolled version's own
            // capture+stopPropagation, just anchored to Radix's listener
            // instead of a second one of our own.
            onEscapeKeyDown={(e) => e.stopPropagation()}
          >
            <div className="sv2-helppop" style={{ position: "static" }}>
              <div className="sv2-ht">
                <Lightbulb size={16} className="sv2-ic" />
                {translate("MC_DECL_V2_HELP_TITLE")}
              </div>
              {/* `sv2-hlead`, NOT the ported `hl`: the stylesheet's bare `.sv2-hl`
                  is the PDF evidence-highlight overlay (position:absolute + the
                  yellow wash), and a class shared with it painted this lead as a
                  floating yellow band underneath step 1. */}
              <div className="sv2-hlead">
                {translate("MC_DECL_V2_HELP_LEAD")}
              </div>
              <ol className="sv2-helpsteps list-none">
                {(
                  [
                    "MC_DECL_V2_HELP_STEP_1",
                    "MC_DECL_V2_HELP_STEP_2",
                    "MC_DECL_V2_HELP_STEP_3",
                  ] as const
                ).map((key, i) => (
                  <li key={key} className="sv2-helpstep">
                    <span className="sv2-n">{i + 1}</span>
                    <div>{translate(key)}</div>
                  </li>
                ))}
              </ol>
              {/* One chip per key, as the design has it — a single run-on
                  sentence is not something a reader scans mid-review. The keys
                  are this workspace's real scheme (`useStudioV2Keyboard`), not
                  the prototype's. */}
              <div className="sv2-hkeys">
                {(
                  [
                    ["↵", "MC_DECL_V2_HELP_KEY_ENTER"],
                    ["↑ ↓", "MC_DECL_V2_HELP_KEY_ARROWS"],
                    ["1 2 3", "MC_DECL_V2_HELP_KEY_ACTIONS"],
                    // W exists only with the guided layer's onNext wired —
                    // listing a dead key would be worse than listing none.
                    ...(showNextShortcut
                      ? ([["W", "MC_DECL_V2_HELP_KEY_NEXT"]] as const)
                      : []),
                    ["Esc", "MC_DECL_V2_HELP_KEY_ESC"],
                    ["?", "MC_DECL_V2_HELP_KEY_HELP"],
                  ] as const
                ).map(([chip, key]) => (
                  <span key={key}>
                    <kbd>{chip}</kbd>
                    {translate(key)}
                  </span>
                ))}
              </div>
              <HelpdeskLink
                categoryId="supplier-studio"
                articleId="erklaerung-pruefen"
                className="mt-3"
              />
              <div className="sv2-hf">
                <span className="sv2-hnote">
                  {translate("MC_DECL_V2_HELP_REOPEN_NOTE")}
                </span>
                <button
                  type="button"
                  className="sv2-btn sv2-btn-sm sv2-btn-primary"
                  onClick={() => handleOpenChange(false)}
                >
                  {translate("MC_DECL_V2_HELP_DISMISS")}
                </button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  },
)

/** Test-only reset: a session boundary this hook cannot see otherwise. */
export function resetHelpPopoverAutoOpen(): void {
  autoOpened = false
  try {
    window.localStorage.removeItem(HELP_SEEN_KEY)
  } catch {
    /* jsdom always has storage; belt and braces */
  }
}
